import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  SelectChangeEvent,
  Autocomplete,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { complaintsService } from '../../services/complaintsService';
import { Customer } from '../../services/customersService';
import { Produit } from '../../services/productsService';
import { Order } from '../../services/ordersService';
import { snackActions } from '../../utils/snackbarUtils';
import api from '../../services/api';

interface NewComplaintFormData {
  subject: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  customer_id: number;
  order_id?: number;
  product_id?: number;
}

const priorityOptions = [
  { value: 'low', label: 'Basse' },
  { value: 'medium', label: 'Moyenne' },
  { value: 'high', label: 'Haute' },
  { value: 'critical', label: 'Critique' },
];

const ComplaintCreate: React.FC = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<NewComplaintFormData>({
    subject: '',
    description: '',
    priority: 'medium',
    customer_id: 0,
  });

  // États pour les données des sélecteurs
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [products, setProducts] = useState<Produit[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Produit | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  useEffect(() => {
    fetchCustomers();
    fetchProducts();
    fetchOrders();
  }, []);

  const fetchCustomers = async () => {
    try {
      setLoadingCustomers(true);
      const { data } = await api.get('/customers?limit=100');
      const apiCustomers = (data.data?.customers || []).map((customer: any) => ({
        id: customer.id,
        user_id: customer.id,
        first_name: customer.first_name,
        last_name: customer.last_name,
        address: customer.address || '',
        city: customer.city || '',
        commune: customer.commune || '',
        email: customer.email,
        phone: customer.phone,
        createdAt: customer.created_at || new Date().toISOString(),
        updatedAt: customer.updated_at || new Date().toISOString(),
        user: {
          id: customer.id,
          email: customer.email,
          phone: customer.phone,
          role: 'customer',
          status: customer.status || 'active'
        }
      }));
      setCustomers(apiCustomers);
    } catch (error) {
      console.error('Erreur lors du chargement des clients:', error);
      snackActions.error('Erreur lors du chargement des clients');
    } finally {
      setLoadingCustomers(false);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoadingProducts(true);
      const { data } = await api.get('/products?limit=100');
      const apiProducts = (data.donnees?.produits || []).map((product: any) => ({
        id: product.id,
        reference: product.reference,
        nom: product.nom,
        description: product.description,
        prix: product.prix,
        cout: product.cout || product.prix * 0.8,
        quantite_stock: product.quantite_stock,
        seuil_alerte: product.seuil_alerte,
        marque_id: product.marque_id,
        categorie_id: product.categorie_id,
        actif: product.actif,
        images: product.images || [],
        specifications: product.specifications || {},
        date_creation: product.createdAt || new Date().toISOString(),
        date_mise_a_jour: product.updatedAt || new Date().toISOString(),
        marque: product.marque || { id: product.marque_id, nom: 'Marque inconnue', description: '', date_creation: '2024-01-01T00:00:00Z' },
        categorie: product.categorie || { id: product.categorie_id, nom: 'Catégorie inconnue', description: '', date_creation: '2024-01-01T00:00:00Z' }
      }));
      setProducts(apiProducts);
    } catch (error) {
      console.error('Erreur lors du chargement des produits:', error);
      snackActions.error('Erreur lors du chargement des produits');
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchOrders = async () => {
    try {
      setLoadingOrders(true);
      const { data } = await api.get('/orders?limit=100');

      // Convertir les données de l'API vers le format attendu
      const apiOrders = (data.data || []).map((order: any) => ({
        id: order.id,
        reference: order.reference || `ORD-${order.id.toString().padStart(4, '0')}`,
        customerId: order.customerId,
        customerName: order.customer ? `${order.customer.first_name} ${order.customer.last_name}` : order.customerName || 'Client inconnu',
        customerEmail: order.customer ? order.customer.email : order.customerEmail,
        orderDate: order.createdAt || new Date().toISOString(),
        status: order.status?.toUpperCase() || 'PENDING',
        paymentStatus: 'PAID', // Valeur par défaut
        subtotal: order.totalAmount || 0,
        taxAmount: 0,
        discountAmount: 0,
        shippingCost: 0,
        totalAmount: order.totalAmount || 0,
        items: order.items || []
      }));
      
      setOrders(apiOrders);
    } catch (error) {
      console.error('Erreur lors du chargement des commandes:', error);
      snackActions.error('Erreur lors du chargement des commandes');
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleBack = () => {
    navigate('/reclamations');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name as string]: value,
    }));
  };

  const handleCustomerChange = (event: any, value: Customer | null) => {
    setSelectedCustomer(value);
    setFormData(prev => ({
      ...prev,
      customer_id: value ? value.id : 0,
    }));
  };

  const handleProductChange = (event: any, value: Produit | null) => {
    setSelectedProduct(value);
    setFormData(prev => ({
      ...prev,
      product_id: value ? value.id : undefined,
    }));
  };

  const handleOrderChange = (event: any, value: Order | null) => {
    setSelectedOrder(value);
    setFormData(prev => ({
      ...prev,
      order_id: value ? value.id : undefined,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.subject.trim() || !formData.description.trim() || !formData.customer_id) {
      snackActions.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      setSaving(true);
      const newComplaint = await complaintsService.createComplaint(formData);
      snackActions.success('Réclamation créée avec succès');
      navigate(`/reclamations/${newComplaint.id}`);
    } catch (error) {
      snackActions.error('Erreur lors de la création de la réclamation');
      console.error('Error creating complaint:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center">
          <IconButton onClick={handleBack} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Nouvelle Réclamation
          </Typography>
        </Box>
        {process.env.NODE_ENV === 'development' && (
          <Button 
            variant="outlined" 
            size="small"
            onClick={() => {
              console.log('Debug - Clients:', customers.length);
              console.log('Debug - Produits:', products.length);
              console.log('Debug - Commandes:', orders.length);
            }}
          >
            Debug ({customers.length}C, {products.length}P, {orders.length}O)
          </Button>
        )}
      </Box>

      <Card variant="outlined">
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Sujet"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                  placeholder="Résumé du problème..."
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  multiline
                  rows={4}
                  variant="outlined"
                  placeholder="Description détaillée du problème..."
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Priorité</InputLabel>
                  <Select
                    value={formData.priority}
                    onChange={handleSelectChange}
                    label="Priorité"
                    name="priority"
                  >
                    {priorityOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={customers}
                  getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
                  value={selectedCustomer}
                  onChange={handleCustomerChange}
                  loading={loadingCustomers}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Client"
                      variant="outlined"
                      required
                      placeholder="Sélectionnez un client..."
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box>
                        <Typography variant="body1">
                          {option.first_name} {option.last_name}
                        </Typography>
                        {option.user?.email && (
                          <Typography variant="caption" color="textSecondary">
                            {option.user.email}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={orders}
                  getOptionLabel={(option) => `${option.reference || 'N/A'} - ${option.customerName}`}
                  value={selectedOrder}
                  onChange={handleOrderChange}
                  loading={loadingOrders}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Commande (optionnel)"
                      variant="outlined"
                      placeholder="Sélectionnez une commande..."
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box>
                        <Typography variant="body1">
                          {option.reference || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Client: {option.customerName} - {option.totalAmount} FCFA
                        </Typography>
                      </Box>
                    </Box>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={products}
                  getOptionLabel={(option) => option.nom}
                  value={selectedProduct}
                  onChange={handleProductChange}
                  loading={loadingProducts}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Produit (optionnel)"
                      variant="outlined"
                      placeholder="Sélectionnez un produit..."
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box>
                        <Typography variant="body1">
                          {option.nom}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Réf: {option.reference} - {option.prix}€
                        </Typography>
                      </Box>
                    </Box>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end" gap={2}>
                  <Button
                    variant="outlined"
                    onClick={handleBack}
                    disabled={saving}
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={saving}
                  >
                    {saving ? 'Création...' : 'Créer la réclamation'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ComplaintCreate;